// Landing page at `/`. The product's storefront, written in the voice
// of a tool you actually use — Modal, Supabase, Sentry — not a marketing
// deck. Keep the mono-first theme; sans-serif marketing-headlines are
// explicitly out (per the UI design memory). Everything here is live DB
// where possible; nothing is faked.
//
// Section order (deliberate — mirrors a reader's "what / proof / how /
// try" scan):
//   1. Hero          — three-line declarative statement + two CTAs
//   2. Open stack    — the known-good tools we're built on (credibility
//                      without pretending we have enterprise customers)
//   3. Recent catches — live malicious completed jobs, clickable
//   4. Stats         — real numbers from the last 50 completed jobs
//   5. What you get  — six capability cards with concrete sub-stats
//   6. How it works  — three numbered steps
//   7. See a report  — a direct link into a real impressive public job
//   8. FAQ           — short, 4-5 Q
//   9. Final CTA     — same primary button as the hero
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { BigFooter } from "@/components/shell/BigFooter";
import { buttonClass, VerdictBanner } from "@/components/ui";
import { useJobs, useSeo } from "@/hooks";
import { count } from "@/lib/format";
import type { Job } from "@/types";

// Hand-picked public job to deep-link to as the "sample report". Pick
// one that shows off the whole pipeline (config extraction + behavior
// tags + suricata). The id is kept in a const so it's easy to swap
// when a better run shows up — this is the one thing on the page I
// can't drive from live-DB without an extra round trip.
const SAMPLE_REPORT_ID = "d084f1bd3aa6ff77-9d29d860015995cd";
const SAMPLE_REPORT_FAMILY = "Lumma";

const CAPABILITIES: { title: string; stat?: string; detail: string }[] = [
  {
    title: "Dynamic analysis",
    stat: "~90 s per run",
    detail: "Fresh Windows 10 / 11 KVM every submission. Every process, file write, registry change, module load, and network flow timestamped. VNC into any live run.",
  },
  {
    title: "Decrypted HTTPS",
    stat: "every TLS flow",
    detail: "Per-job MITM proxy with a CA baked into the guest trust store. Request and response bodies land in cleartext — read the C2 handshake, not the ciphertext.",
  },
  {
    title: "Config extraction",
    stat: "40+ families",
    detail: "Rust registry + MACO community parsers. NanoCore, AsyncRAT, Remcos, DCRat, Lumma, RedLine, Formbook, AgentTesla, Quasar, Stealc — C2, mutex, keys, campaign, raw dump.",
  },
  {
    title: "Network inspection",
    stat: "ET Open rules",
    detail: "Suricata runs over the pcap. Signatures badge on the destination row. Hex view of any flow's bytes — reassembled TCP, directional.",
  },
  {
    title: "Third-party intel",
    stat: "6 providers",
    detail: "VirusTotal · Triage · Hybrid Analysis · MalwareBazaar · YARAify · MalShare joined into one view per sample sha256.",
  },
  {
    title: "Yours end-to-end",
    stat: "self-host coming",
    detail: "Replayable artifacts in your own S3. Postgres metadata. Kafka event bus. Self-hosted deploys land soon — email neel@neels.dev for early access.",
  },
];

const STACK = [
  "Suricata",
  "YARA-X",
  "ET Open",
  "MITRE ATT&CK",
  "Volatility",
  "MACO",
  "libvirt",
  "MinIO",
  "Kafka",
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "What file types can I submit?",
    a: "Windows PE (.exe, .dll, .sys), script runnables (.ps1, .bat, .vbs, .js, .hta, .wsf, .msi, .lnk), and archives (zip, 7z, rar, tar — password-protected or not, 'infected' is tried automatically). Also URLs.",
  },
  {
    q: "How long does analysis take?",
    a: "~90 seconds by default. Configurable up to 30 minutes per run. The report lands incrementally as the analysis streams in.",
  },
  {
    q: "Can I interact with the sample while it runs?",
    a: "Yes. Live VNC into the guest from the report page. Click, type, close dialogs — the sample sees your input in real time.",
  },
  {
    q: "What about ELF / Mach-O / scripts for other OSes?",
    a: "Windows-only right now. ELF and Mach-O binaries in an archive are silently dropped during extraction.",
  },
  {
    q: "Is the data mine?",
    a: "Artifacts live in your own object storage (MinIO or any S3-compatible). Metadata in your Postgres. Nothing is sent to third parties except intel lookups you opt into.",
  },
];

function CatchCard({ job }: { job: Job }) {
  const v = job.verdict;
  return (
    <Link
      to={`/jobs/${job.id}`}
      viewTransition
      className="group flex min-w-0 flex-col gap-2 border border-rule-row bg-paper p-4 transition-colors hover:border-ink focus-visible:border-ink focus-visible:outline-none"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate font-mono text-ui text-ink group-hover:underline" translate="no">
          {job.filename ?? job.sample_sha?.slice(0, 12) ?? "sample"}
        </span>
        {v && <VerdictBanner v={v.label} score={v.score} />}
      </div>
      <div className="meta tabular-nums">
        {job.sample_sha ? <span className="truncate">{job.sample_sha.slice(0, 12)}…</span> : null}
        {v?.max_severity && <span className="ml-2">· {v.max_severity}</span>}
        {typeof v?.tag_count === "number" && v.tag_count > 0 && <span className="ml-2">· {count(v.tag_count)} tag{v.tag_count === 1 ? "" : "s"}</span>}
      </div>
    </Link>
  );
}

function Hero() {
  return (
    <section className="relative min-w-0 pt-14 sm:pt-20" aria-labelledby="hero-head">
      <p className="lens-eyebrow">The analyst-grade sandbox · in beta</p>
      <h1
        id="hero-head"
        className="mt-5 font-mono text-ink"
        style={{ fontSize: "clamp(40px, 6.5vw, 84px)", lineHeight: 0.98, letterSpacing: "-0.035em" }}
      >
        Watch malware run.
        <br />
        Read its traffic.
        <br />
        <span className="text-ink-soft">Pull its config.</span>
      </h1>
      <p className="mt-7 max-w-[680px] text-ui text-ink" style={{ fontSize: "clamp(18px, 1.5vw, 22px)", lineHeight: 1.45 }}>
        Most sandboxes give you a verdict.{" "}
        <span className="text-ink-soft">malbox gives you the proof.</span>
      </p>
      <p className="mt-5 max-w-[640px] text-ui text-ink-soft" style={{ fontSize: "clamp(16px, 1.3vw, 18px)", lineHeight: 1.55 }}>
        Zero agents in the guest. Decrypted HTTPS. Live interactive VMs. Every byte we see, you
        see too — process events, pcap, memory, extracted C2 config, timestamped and joined on
        one page. In about two minutes.
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link to="/submit" viewTransition className={buttonClass({ variant: "cta", size: "lg" })}>
          Try it →
        </Link>
        <Link to={`/jobs/${SAMPLE_REPORT_ID}`} viewTransition className={buttonClass({ variant: "primary", size: "lg" })}>
          See a sample report
        </Link>
        <a href="mailto:neel@neels.dev" className={buttonClass({ variant: "ghost", size: "lg" })}>
          Contact
        </a>
      </div>
    </section>
  );
}

function OpenStack() {
  return (
    <section className="mt-20 min-w-0 border-y border-rule-row py-6" aria-label="Built on open telemetry">
      <p className="meta text-ink-soft">Built on open telemetry you can trust</p>
      <ul className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-2 font-mono text-ui text-ink">
        {STACK.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </section>
  );
}

function Manifesto() {
  const claims = [
    {
      head: "Agent-free guests",
      body: "Nothing to detect, nothing to patch out. The agent runs on the host and reads KVM telemetry — the sample sees a stock Windows that boots the way a victim's would.",
    },
    {
      head: "Decrypted HTTPS by default",
      body: "A per-job MITM proxy with a baked-in CA. You read the C2 request and response bodies in cleartext — the handshake, not the ciphertext.",
    },
    {
      head: "Yours to own",
      body: "Hosted today; self-host coming soon. Your bytes land in your bucket, your metadata in your Postgres. No pay-per-lookup wall between you and your own data.",
    },
  ];
  return (
    <section className="mt-20 min-w-0" aria-labelledby="why-head">
      <p className="lens-eyebrow">What makes it different</p>
      <h2
        id="why-head"
        className="mt-3 font-mono text-ink"
        style={{ fontSize: "clamp(28px, 3vw, 44px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}
      >
        The sandbox we wanted,{" "}
        <span className="text-ink-soft">built the way we'd build infrastructure.</span>
      </h2>
      <div className="mt-8 grid gap-x-8 gap-y-8 sm:grid-cols-3">
        {claims.map((c) => (
          <div key={c.head} className="min-w-0 border-t border-rule-row pt-4">
            <h3 className="font-mono text-ui text-ink">{c.head}</h3>
            <p className="mt-2 text-ui text-ink-soft">{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function LiveCatches({ catches }: { catches: Job[] }) {
  if (catches.length === 0) return null;
  return (
    <section className="mt-16 min-w-0" aria-labelledby="live-head">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="live-head" className="font-mono text-ink" style={{ fontSize: "clamp(24px, 2.4vw, 32px)", letterSpacing: "-0.02em" }}>Recent catches</h2>
        <Link to="/jobs" viewTransition className="meta hover:underline">
          All analyses →
        </Link>
      </div>
      <p className="mt-2 max-w-[640px] text-ui text-ink-soft">
        Live from the fleet. Click any to see its full report — behavior tags, extracted config, decrypted HTTP, pcap, screenshots.
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {catches.map((j) => <CatchCard key={j.id} job={j} />)}
      </div>
    </section>
  );
}

function StatsStrip({ jobs }: { jobs: readonly Job[] | undefined }) {
  const total = jobs?.length ?? 0;
  const malicious = jobs?.filter((j) => j.verdict?.label === "malicious").length ?? 0;
  const suspicious = jobs?.filter((j) => j.verdict?.label === "suspicious").length ?? 0;
  if (total === 0) return null;
  const rate = total > 0 ? Math.round((malicious / total) * 100) : 0;
  return (
    <section className="mt-16 grid grid-cols-2 gap-x-6 gap-y-6 border-y border-rule-row py-6 sm:grid-cols-4" aria-label="Fleet stats">
      <Stat n={count(total)} label="recent analyses" />
      <Stat n={count(malicious)} label="malicious" tone="high" />
      <Stat n={count(suspicious)} label="suspicious" tone="medium" />
      <Stat n={`${rate}%`} label="catch rate" />
    </section>
  );
}

function Stat({ n, label, tone }: { n: string; label: string; tone?: "high" | "medium" }) {
  const color = tone === "high" ? "text-high-text" : tone === "medium" ? "text-medium-text" : "text-ink";
  return (
    <div>
      <div className={`font-mono tabular-nums ${color}`} style={{ fontSize: "clamp(32px, 3vw, 44px)", lineHeight: 1, letterSpacing: "-0.02em" }}>
        {n}
      </div>
      <div className="meta mt-2">{label}</div>
    </div>
  );
}

function Capabilities() {
  return (
    <section className="mt-20 min-w-0" aria-labelledby="cap-head">
      <h2 id="cap-head" className="font-mono text-ink" style={{ fontSize: "clamp(28px, 3vw, 40px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}>
        Everything you need to understand what a sample does.
      </h2>
      <p className="mt-3 max-w-[720px] text-ui text-ink-soft">
        One submission, every signal — telemetry, network, memory, static, intel — joined on the
        sample's sha256. No tabs between tools.
      </p>
      <div className="mt-8 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
        {CAPABILITIES.map((c) => (
          <div key={c.title} className="min-w-0 border-t border-rule-row pt-4">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="font-mono text-ui text-ink">{c.title}</h3>
              {c.stat && <span className="meta tabular-nums whitespace-nowrap">{c.stat}</span>}
            </div>
            <p className="mt-2 text-ui text-ink-soft">{c.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Drop a sample", detail: "Drag a Windows PE, paste a URL, or upload a password-protected archive. Already-seen hashes skip the upload." },
    { n: "02", title: "Boot and run", detail: "Fresh Windows VM, agent on the host (not in the guest), 90-second observation window by default. Live VNC available." },
    { n: "03", title: "Read the report", detail: "Behavior tags with MITRE mappings, extracted config, Suricata alerts on the pcap, decrypted HTTP bodies, screenshots, replay." },
  ];
  return (
    <section className="mt-20 min-w-0" aria-labelledby="how-head">
      <p className="lens-eyebrow">How it works</p>
      <h2 id="how-head" className="mt-3 font-mono text-ink" style={{ fontSize: "clamp(28px, 3vw, 40px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}>
        From a file on your desktop to a full report in two minutes.
      </h2>
      <div className="mt-8 grid gap-6 sm:grid-cols-3">
        {steps.map((s) => (
          <div key={s.n} className="min-w-0">
            <div className="font-mono tabular-nums text-ink-mute" style={{ fontSize: "28px", letterSpacing: "-0.02em" }}>{s.n}</div>
            <h3 className="mt-2 font-mono text-ui text-ink">{s.title}</h3>
            <p className="mt-2 text-ui text-ink-soft">{s.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SeeAReport() {
  return (
    <section className="mt-20 min-w-0 border border-rule-strong bg-paper p-8" aria-labelledby="sample-head">
      <p className="lens-eyebrow">Sample report</p>
      <h2 id="sample-head" className="mt-3 font-mono text-ink" style={{ fontSize: "clamp(24px, 2.5vw, 36px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}>
        A {SAMPLE_REPORT_FAMILY} catch, start to finish.
      </h2>
      <p className="mt-3 max-w-[640px] text-ui text-ink-soft">
        Behavior tree, extracted config (C2, mutex, build time), YARA + Suricata hits, decrypted HTTPS,
        process dumps, memory findings, third-party intel — all on one page.
      </p>
      <div className="mt-5">
        <Link to={`/jobs/${SAMPLE_REPORT_ID}`} viewTransition className={buttonClass({ variant: "primary", size: "lg" })}>
          Open the report →
        </Link>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="mt-20 min-w-0" aria-labelledby="faq-head">
      <h2 id="faq-head" className="font-mono text-ink" style={{ fontSize: "clamp(24px, 2.4vw, 32px)", letterSpacing: "-0.02em" }}>Frequently asked</h2>
      <dl className="mt-6 divide-y divide-rule-row border-y border-rule-row">
        {FAQ.map((f) => (
          <details key={f.q} className="group py-4">
            <summary className="flex cursor-pointer list-none items-start justify-between gap-6 font-mono text-ui text-ink">
              <span>{f.q}</span>
              <span className="text-ink-mute transition-transform group-open:rotate-45" aria-hidden="true">+</span>
            </summary>
            <dd className="mt-3 max-w-[720px] text-ui text-ink-soft">{f.a}</dd>
          </details>
        ))}
      </dl>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="mt-20 min-w-0 border border-rule-strong bg-paper px-6 py-12 text-center">
      <h2 className="font-mono text-ink" style={{ fontSize: "clamp(28px, 3vw, 44px)", lineHeight: 1.05, letterSpacing: "-0.025em" }}>
        Stop reading verdicts.
        <br />
        <span className="text-ink-soft">Start reading bodies.</span>
      </h2>
      <p className="mt-4 text-ui text-ink-soft">
        No sign-in required. First report in about two minutes.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/submit" viewTransition className={buttonClass({ variant: "cta", size: "lg" })}>
          Submit a sample →
        </Link>
        <Link to="/jobs" viewTransition className={buttonClass({ variant: "ghost", size: "lg" })}>
          Browse recent analyses
        </Link>
      </div>
    </section>
  );
}

export function LandingPage() {
  const { data } = useJobs(50, { refetchMs: 30_000 });
  const catches = (data ?? [])
    .filter((j) => j.state === "completed" && j.verdict?.label === "malicious")
    .slice(0, 4);
  // FAQPage schema → the five Q/A pairs on the landing become rich
  // results in Google. Memoized so useSeo's effect doesn't thrash on
  // every render.
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
    [],
  );
  useSeo({
    title: "malbox — The analyst-grade malware sandbox",
    description:
      "Online malware analysis sandbox. Watch Windows samples run, read decrypted HTTPS traffic, pull extracted C2 config, and inspect pcap, memory, YARA, Suricata, and MITRE ATT&CK — in about two minutes. Free during beta.",
    path: "/",
    keywords:
      "malware sandbox, malware analysis sandbox, online malware sandbox, dynamic malware analysis, interactive malware sandbox, free malware sandbox, Windows malware sandbox, decrypted HTTPS malware, C2 config extractor, YARA online, Suricata, MITRE ATT&CK, malware triage, PE analysis, malware report",
    jsonLd,
  });
  return (
    <>
      <div className="mx-auto flex min-w-0 max-w-[1200px] flex-col px-5 pb-16 sm:px-8">
        <Hero />
        <OpenStack />
        <Manifesto />
        <LiveCatches catches={catches} />
        <StatsStrip jobs={data} />
        <Capabilities />
        <HowItWorks />
        <SeeAReport />
        <FaqSection />
        <FinalCTA />
      </div>
      <BigFooter />
    </>
  );
}
